import { selectors } from '../../Stream';
import { getDataEntities } from '../../redux/entities/selectors';
import { PapiGallection, PapiDeviation } from '@wix/da-papi-types';
import {
  Collection,
  CollectionStoreState,
  CollectionStoreSlice,
  CollectionEntities,
  ChangeType,
} from './types';
import { reduxKey } from './constants';
import { getStreamId } from './helpers';

function getStoreSlice(state: CollectionStoreState): CollectionStoreSlice {
  return (
    state[reduxKey] || {
      isNewCollectionSaving: false,
      deviations: {},
    }
  );
}

function getUserCollectionEntities(
  state: CollectionStoreState
): CollectionEntities['collections'] {
  const { collections }: { collections?: { [k: number]: PapiGallection } } =
    getDataEntities(state);
  if (!collections) {
    return {};
  }

  return collections;
}

function getCollectionsRaw(
  state: CollectionStoreState,
  isPrivate
): PapiGallection[] {
  const collectionIds: number[] =
    selectors.getItems(state, getStreamId(isPrivate)) || [];
  if (!collectionIds.length) {
    return [];
  }

  return getCollectionsByFolderId(state, collectionIds);
}

function getCollectionsByFolderId(
  state: CollectionStoreState,
  folderIds: number[]
) {
  const collections = getUserCollectionEntities(state);
  return folderIds
    .filter(id => collections.hasOwnProperty(id))
    .map(collectionId => collections[collectionId]);
}

function getChangeType(
  added: number,
  removed: number,
  kept: number
): ChangeType {
  if (!added) {
    if (!removed) {
      return 'none';
    }

    if (!kept) {
      return 'removeAll';
    }

    if (removed === 1) {
      return 'removeOne';
    }

    return 'removeSome';
  }

  if (removed) {
    return 'misc';
  }

  if (added > 1) {
    return 'addMany';
  }

  return 'addOne';
}

function getDeviationState(
  state: CollectionStoreState,
  deviationId: number
): CollectionStoreSlice['deviations'][0] | undefined {
  const slice = getStoreSlice(state);
  return slice?.deviations[deviationId];
}

export function getCollection(state: CollectionStoreState, folderId: number) {
  return getUserCollectionEntities(state)[folderId];
}

export function getUserCollections(
  state: CollectionStoreState,
  deviationId: number,
  isPrivateCollections: boolean
) {
  const slice = getDeviationState(state, deviationId);

  const selectedCollectionsKey = isPrivateCollections
    ? 'selectedPrivateCollections'
    : 'selectedCollections';

  const collections: Collection[] = slice
    ? getCollectionsRaw(state, isPrivateCollections).map(collection => ({
        ...collection,
        isSelected: slice[selectedCollectionsKey].includes(collection.folderId),
      }))
    : [];

  const streamId = getStreamId(isPrivateCollections);
  const isInitialized = isPrivateCollections
    ? slice?.isInitializedForPrivateCollecting
    : slice?.isInitializedForCollecting;

  const streamInfo = {
    streamId,
    isInitialized: !!isInitialized,
    hasMore: selectors.hasMore(state, streamId) as boolean,
    isFetching: selectors.isFetching(state, streamId) as boolean,
    errorMsg: selectors.getErrorMsg(state, streamId) as string,
    collections,
  };

  return streamInfo;
}

export function getTotalCountOfCollectedDeviations(
  state: CollectionStoreState,
  deviationId: number,
  isPrivateCollections: boolean
) {
  const { collections } = getUserCollections(
    state,
    deviationId,
    isPrivateCollections
  );
  return collections.reduce((acc, collection) => acc + collection.size, 0);
}

export function getUserCollectionsHasMore(
  state: CollectionStoreState,
  deviationId: number,
  isPrivateCollections: boolean
) {
  return !!getUserCollections(state, deviationId, isPrivateCollections)
    ?.hasMore;
}

export function getUserCollectionsCount(
  state: CollectionStoreState,
  deviationId: number,
  isPrivateCollections: boolean
): number {
  return (
    getUserCollections(state, deviationId, isPrivateCollections)?.collections
      ?.length ?? 0
  );
}

export function getIsNewCollectionSaving(state: CollectionStoreState) {
  return getStoreSlice(state).isNewCollectionSaving;
}

export function getUpdatedEntities(
  state: CollectionStoreState,
  deviation: PapiDeviation,
  isPrivateCollections: boolean
) {
  const entities: CollectionEntities = {
    collections: {},
  };
  const deviationState = getDeviationState(state, deviation.deviationId);

  const currentFolders = deviationState?.savedCollections ?? [];
  const selectedFolders =
    deviationState?.[
      isPrivateCollections
        ? 'selectedPrivateCollections'
        : 'selectedCollections'
    ] ?? [];

  const addedFolders = selectedFolders.filter(
    id => !currentFolders.includes(id)
  );
  const removedFolders = currentFolders.filter(
    id => !selectedFolders.includes(id)
  );

  const collections = getUserCollectionEntities(state);
  const updateFolderSize = (sizeChange: number, folderId: number) => {
    const collection = collections[folderId];
    if (!collection) {
      return;
    }
    const newSize = collection.size + sizeChange;
    entities.collections[folderId] = {
      ...collection,
      size: newSize,
      thumb: newSize === 0 ? undefined : collection.thumb || deviation,
    };
  };

  addedFolders.forEach(id => updateFolderSize(1, id));
  removedFolders.forEach(id => updateFolderSize(-1, id));

  return entities;
}

export function getNewFolderNameBlacklist(
  state: CollectionStoreState,
  isPrivateCollections
) {
  const collections = getCollectionsRaw(state, isPrivateCollections);
  return collections.map(collection => collection.name);
}

export function getIsDeviationInitialized(
  state: CollectionStoreState,
  deviationId: number,
  isPrivateCollections: boolean
) {
  const slice = getStoreSlice(state);
  return (
    slice.deviations.hasOwnProperty(deviationId) &&
    (isPrivateCollections
      ? slice.deviations[deviationId].isInitializedForPrivateCollecting
      : slice.deviations[deviationId].isInitializedForCollecting)
  );
}

export function getSelectedFolderIds(
  state: CollectionStoreState,
  deviationId: number,
  isPrivateCollections: boolean
) {
  const slice = getDeviationState(state, deviationId);
  return (
    (isPrivateCollections
      ? slice?.selectedPrivateCollections
      : slice?.selectedCollections) ?? []
  );
}

export function getSpecialDefaultFolderId(
  state: CollectionStoreState,
  isPrivateCollections
) {
  const specialFolderName = isPrivateCollections ? 'Private' : 'Featured';
  const collections = getCollectionsRaw(state, isPrivateCollections);
  const specialFolder = collections.find(f => f.name === specialFolderName);
  return specialFolder && specialFolder.folderId;
}

export function getChangeDetails(
  state: CollectionStoreState,
  deviationId: number,
  isPrivateCollections: boolean
) {
  const selectedCollectionsKey = isPrivateCollections
    ? 'selectedPrivateCollections'
    : 'selectedCollections';
  const {
    [selectedCollectionsKey]: selected = [],
    savedCollections: saved = [],
    isFavourite: wasFavourite = false,
    isPrivatelyCollected: wasPrivatelyCollected = false,
  } = getDeviationState(state, deviationId) ?? {};

  const added = selected.filter(f => !saved.includes(f));
  const removed = saved.filter(f => !selected.includes(f));
  const kept = saved.filter(f => selected.includes(f));

  const willBePrivatelyCollected = isPrivateCollections && selected.length > 0;
  const willBeFavourite = !isPrivateCollections && selected.length > 0;

  return {
    added,
    removed,
    selected,
    saved,
    changeType: getChangeType(added.length, removed.length, kept.length),
    wasFavourite,
    willBeFavourite,
    wasPrivatelyCollected,
    willBePrivatelyCollected,
  };
}

// TODO: just kill this, it's terrible for perf
// @deprecated
export function getDeviationPatchedWithStoreState(
  state: CollectionStoreState,
  deviation: PapiDeviation
): PapiDeviation {
  const slice = getStoreSlice(state).deviations[deviation.deviationId];
  if (!slice) {
    return deviation;
  }

  return {
    ...deviation,
    isFavourited: slice.isFavourite,
    stats: deviation.stats && {
      ...deviation.stats,
      favourites: slice.faveCount,
    },
  };
}

export function getIsFavourite(
  state: CollectionStoreState,
  deviation: PapiDeviation
) {
  const slice = getDeviationState(state, deviation.deviationId);
  return slice?.isFavourite ?? deviation.isFavourited ?? false;
}

export function getIsPrivatelyCollected(
  state: CollectionStoreState,
  deviation: PapiDeviation
) {
  const slice = getDeviationState(state, deviation.deviationId);
  return (
    slice?.isPrivatelyCollected ??
    deviation.extended?.isPrivatelyCollected ??
    false
  );
}

export function getFaveCount(
  state: CollectionStoreState,
  deviation: PapiDeviation
) {
  const slice = getDeviationState(state, deviation.deviationId);
  return slice?.faveCount ?? deviation.stats?.favourites ?? 0;
}

export function getIsFaveLoading(
  state: CollectionStoreState,
  deviation: PapiDeviation
) {
  const slice = getDeviationState(state, deviation.deviationId);
  return slice?.isLoading ?? false;
}
